import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  CheckCircle2,
  ArrowRight,
  Clock,
  Sparkles
} from 'lucide-react';
import { pricingPackages, appPricingPackages, faqItems } from '../data/mock';

const Pricing = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('websites');

  const formatPrice = (price, period, currency) => {
    if (period === 'ab') {
      return `${period} ${currency}${price.toLocaleString('de-DE')}`;
    }
    return `${currency}${price.toLocaleString('de-DE')}`;
  };

  const renderPricingCard = (pkg) => (
    <Card
      key={pkg.id}
      className={`relative border-2 transition-all duration-300 hover:shadow-xl ${
        pkg.highlighted
          ? 'border-blue-600 shadow-lg scale-[1.02]'
          : 'border-slate-200 hover:border-blue-300'
      }`}
    >
      {pkg.highlighted && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center gap-1 px-4 py-1 rounded-full bg-blue-600 text-white text-sm font-medium">
            <Sparkles size={14} />
            Beliebt
          </span>
        </div>
      )}
      <CardHeader className="pb-4">
        <CardTitle className="text-xl">{pkg.name}</CardTitle>
        <CardDescription>{pkg.description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-6">
          <span className="text-4xl font-bold text-slate-900">
            {formatPrice(pkg.price, pkg.period, pkg.currency)}
          </span>
          {pkg.period !== 'ab' && (
            <span className="text-slate-500 ml-2">({pkg.period})</span>
          )}
        </div>

        <div className="flex items-center gap-2 text-slate-600 mb-6">
          <Clock size={16} className="text-blue-600" />
          <span className="text-sm">Lieferzeit: {pkg.deliveryTime}</span>
        </div>

        <ul className="space-y-3 mb-8">
          {pkg.features.map((feature, index) => (
            <li key={index} className="flex items-start gap-3">
              <CheckCircle2 className="text-green-500 flex-shrink-0 mt-0.5" size={18} />
              <span className="text-slate-700 text-sm">{feature}</span>
            </li>
          ))}
        </ul>

        <Link to="/kontakt">
          <Button
            className={`w-full ${
              pkg.highlighted
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-slate-100 hover:bg-slate-200 text-slate-900'
            }`}
          >
            Paket wählen
            <ArrowRight className="ml-2" size={18} />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-20 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-300 text-sm font-medium mb-6">
            {t('pricing.title')}
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            {t('pricing.subtitle')}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400"> {t('pricing.subtitleHighlight', 'Qualität')}</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            {t('pricing.description')}
          </p>
        </div>
      </section>

      {/* Pricing Tables */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex justify-center mb-12">
              <TabsList className="grid grid-cols-2 w-full max-w-md bg-slate-100 p-1 rounded-xl">
                <TabsTrigger
                  value="websites"
                  className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm py-3"
                >
                  Webseiten
                </TabsTrigger>
                <TabsTrigger
                  value="apps"
                  className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm py-3"
                >
                  Web Apps
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="websites" className="mt-0">
              <div className="grid md:grid-cols-3 gap-8">
                {pricingPackages.map(renderPricingCard)}
              </div>
            </TabsContent>

            <TabsContent value="apps" className="mt-0">
              <div className="grid md:grid-cols-3 gap-8">
                {appPricingPackages.map(renderPricingCard)}
              </div>
            </TabsContent>
          </Tabs>

          {/* Custom Quote */}
          <div className="mt-16 text-center">
            <p className="text-slate-600 mb-4">
              Haben Sie spezielle Anforderungen? Kein Problem!
            </p>
            <Link to="/kontakt">
              <Button variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50">
                Individuelles Angebot anfordern
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Bei jedem Projekt inklusive
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Unabhängig vom gewählten Paket erhalten Sie bei uns immer:
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Responsive Design', desc: 'Perfekte Darstellung auf allen Geräten' },
              { title: 'SEO-Grundlagen', desc: 'Optimiert für Suchmaschinen' },
              { title: 'SSL-Zertifikat', desc: 'Sichere Verbindung für Ihre Besucher' },
              { title: 'DSGVO-konform', desc: 'Datenschutz nach EU-Standards' },
              { title: 'Performance-Optimierung', desc: 'Schnelle Ladezeiten garantiert' },
              { title: 'Cross-Browser Support', desc: 'Funktioniert in allen Browsern' },
              { title: 'Dokumentation', desc: 'Detaillierte Anleitungen zur Nutzung' },
              { title: 'Persönlicher Support', desc: 'Direkter Ansprechpartner' }
            ].map((item, index) => (
              <Card key={index} className="border-0 shadow-sm hover:shadow-md transition-shadow duration-200">
                <CardContent className="p-6">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                    <CheckCircle2 className="text-blue-600" size={22} />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">FAQ</span>
            <h2 className="text-3xl font-bold text-slate-900 mt-3 mb-4">
              Häufig gestellte Fragen
            </h2>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border-b border-slate-200">
                <AccordionTrigger className="text-left font-medium text-slate-900 hover:text-blue-600 py-5">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-5">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Bereit loszulegen?
          </h2>
          <p className="text-blue-100 mb-8 text-lg">
            Kontaktieren Sie uns für eine kostenlose Erstberatung und erhalten Sie ein individuelles Angebot.
          </p>
          <Link to="/kontakt">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50">
              Jetzt Projekt starten
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
